import { t } from "i18next";
import { match, P } from "ts-pattern";

import { FieldBuilder, MultiPageFormBuilder } from "../../formBuilder/index.js";
import {
  ethnicitySchema,
  forenameSchema,
  FormSectionSchema,
  genderSchema,
  IncidentFormTypeSchema,
  LocationSchema,
  longTextSchema,
  nonFutureDateSchema,
  selfDeclaredEthnicitySchema,
  shortTextSchema,
  surnameSchema,
  yesNoSchema,
  yesOnlySchema,
  type CreateIncidentMinimumFields,
} from "../../types/index.js";
import { tk } from "../../types/Translation.js";

export const formSpecification = new MultiPageFormBuilder()
  .withFieldAlwaysVisible({
    fieldName: "personForename", // 136-person-1.0
    type: "textbox",
    ...new FieldBuilder().withUnknowableSchema(forenameSchema).build(),
    label: tk("createIncident.personForename"),
    defaultValue: ({ incidentWorkItem }) => {
      const createIncidentForm = incidentWorkItem?.forms.find(
        (form) => form.formType === IncidentFormTypeSchema.enum.createIncident,
      );
      const createIncidentSectionData = createIncidentForm?.sections.find(
        (section) => section.id === FormSectionSchema.enum.createIncidentMain,
      )?.data as Partial<CreateIncidentMinimumFields> | undefined;

      const personForename = createIncidentSectionData?.personForename;
      return personForename ? personForename : null;
    },
  })
  .withFieldAlwaysVisible({
    fieldName: "personSurname", // 136-person-1.1
    type: "textbox",
    ...new FieldBuilder().withUnknowableSchema(surnameSchema).build(),
    label: tk("createIncident.personSurname"),
    defaultValue: ({ incidentWorkItem }) => {
      const createIncidentForm = incidentWorkItem?.forms.find(
        (form) => form.formType === IncidentFormTypeSchema.enum.createIncident,
      );
      const createIncidentSectionData = createIncidentForm?.sections.find(
        (section) => section.id === FormSectionSchema.enum.createIncidentMain,
      )?.data as Partial<CreateIncidentMinimumFields> | undefined;

      const personSurname = createIncidentSectionData?.personSurname;
      return personSurname ? personSurname : null;
    },
  })
  .withFieldAlwaysVisible({
    fieldName: "personAddress", // 136-person-2.0
    type: "location",
    ...new FieldBuilder().withUnknowableSchema(LocationSchema).build(),
    label: tk("personDetails.personAddress"),
  })
  .withFieldConditionallyVisible({
    fieldName: "confirmPersonAddress", // 136-person-2.0.1
    type: "checkbox",
    ...new FieldBuilder().withSchema(yesOnlySchema).captureValueLabel().build(),
    label: tk("personDetails.confirmPersonAddress"),
    isVisible: ({ formValues }) =>
      match(formValues.personAddress)
        .with({ value: P._, isUnknown: true }, () => false)
        .with(undefined, { value: P._, isUnknown: false }, () => true)
        .exhaustive(),
  })
  .withFieldAlwaysVisible({
    fieldName: "dateOfBirth", // 136-person-3.0
    type: "date",
    label: tk("personDetails.dob"),
    disableFuture: true,
    ...new FieldBuilder().withUnknowableSchema(nonFutureDateSchema).build(),
  })
  .withFieldConditionallyVisible({
    fieldName: "satisfiedPersonIsOver18", // 136-person-3.0.1
    type: "radio",
    label: tk("personDetails.satisfiedPersonIsOver18"),
    ...new FieldBuilder().withSchema(yesNoSchema).captureValueLabel().build(),
    options: [
      {
        label: tk("common.yes"),
        value: "yes",
      },
      {
        label: tk("common.no"),
        value: "no",
      },
    ],
    isVisible: ({ formValues }) =>
      match(formValues.dateOfBirth)
        .with(undefined, { value: P._, isUnknown: false }, () => false)
        .with({ value: P._, isUnknown: true }, () => true)
        .exhaustive(),
  })
  .withFieldAlwaysVisible({
    fieldName: "gender", // 136-person-5.0
    type: "dropdown",
    ...new FieldBuilder().withSchema(genderSchema).captureValueLabel().build(),
    label: tk("personDetails.gender.title"),
    options: [
      {
        label: tk("personDetails.gender.options.male"),
        value: "male",
      },
      {
        label: tk("personDetails.gender.options.female"),
        value: "female",
      },
      {
        label: tk("personDetails.gender.options.nonBinary"),
        value: "nonBinary",
      },
      {
        label: tk("personDetails.gender.options.preferNotToSay"),
        value: "preferNotToSay",
      },
    ],
  })
  .withFieldAlwaysVisible({
    fieldName: "ethnicAppearance", // 136-person-6.0
    type: "dropdown",
    ...new FieldBuilder()
      .withSchema(ethnicitySchema)
      .captureValueLabel()
      .build(),
    label: tk("personDetails.ethnicity.title"),
    options: [
      {
        label: tk("personDetails.ethnicity.options.IC1"),
        value: "IC1",
      },
      {
        label: tk("personDetails.ethnicity.options.IC2"),
        value: "IC2",
      },
      {
        label: tk("personDetails.ethnicity.options.IC3"),
        value: "IC3",
      },
      {
        label: tk("personDetails.ethnicity.options.IC4"),
        value: "IC4",
      },
      {
        label: tk("personDetails.ethnicity.options.IC5"),
        value: "IC5",
      },
      {
        label: tk("personDetails.ethnicity.options.IC6"),
        value: "IC6",
      },
      {
        label: tk("personDetails.ethnicity.options.IC7"),
        value: "IC7",
      },
    ],
  })
  .withFieldAlwaysVisible({
    fieldName: "selfDeclaredEthnicity", // 136-person-6.1.1
    type: "dropdown",
    ...new FieldBuilder()
      .withSchema(selfDeclaredEthnicitySchema)
      .captureValueLabel()
      .build(),
    label: tk("personDetails.selfDeclaredEthnicity.title"),
    options: [
      {
        label: tk("personDetails.selfDeclaredEthnicity.options.whiteEnglish"),
        value: "whiteEnglish",
      },
      {
        label: tk("personDetails.selfDeclaredEthnicity.options.whiteIrish"),
        value: "whiteIrish",
      },
      {
        label: tk("personDetails.selfDeclaredEthnicity.options.whiteGypsy"),
        value: "whiteGypsy",
      },
      {
        label: tk("personDetails.selfDeclaredEthnicity.options.whiteRoma"),
        value: "whiteRoma",
      },
      {
        label: tk("personDetails.selfDeclaredEthnicity.options.whiteOther"),
        value: "whiteOther",
      },
      {
        label: tk(
          "personDetails.selfDeclaredEthnicity.options.mixedBlackCaribbean",
        ),
        value: "mixedBlackCaribbean",
      },
      {
        label: tk(
          "personDetails.selfDeclaredEthnicity.options.mixedBlackAfrican",
        ),
        value: "mixedBlackAfrican",
      },
      {
        label: tk("personDetails.selfDeclaredEthnicity.options.mixedAsian"),
        value: "mixedAsian",
      },
      {
        label: tk("personDetails.selfDeclaredEthnicity.options.mixedOther"),
        value: "mixedOther",
      },
      {
        label: tk("personDetails.selfDeclaredEthnicity.options.asianEnglish"),
        value: "asianEnglish",
      },
      {
        label: tk("personDetails.selfDeclaredEthnicity.options.asianIndian"),
        value: "asianIndian",
      },
      {
        label: tk("personDetails.selfDeclaredEthnicity.options.asianPakistani"),
        value: "asianPakistani",
      },
      {
        label: tk(
          "personDetails.selfDeclaredEthnicity.options.asianBangladeshi",
        ),
        value: "asianBangladeshi",
      },
      {
        label: tk("personDetails.selfDeclaredEthnicity.options.asianChinese"),
        value: "asianChinese",
      },
      {
        label: tk("personDetails.selfDeclaredEthnicity.options.asianOther"),
        value: "asianOther",
      },
      {
        label: tk("personDetails.selfDeclaredEthnicity.options.blackEnglish"),
        value: "blackEnglish",
      },
      {
        label: tk("personDetails.selfDeclaredEthnicity.options.blackAfrican"),
        value: "blackAfrican",
      },
      {
        label: tk("personDetails.selfDeclaredEthnicity.options.blackCaribbean"),
        value: "blackCaribbean",
      },
      {
        label: tk("personDetails.selfDeclaredEthnicity.options.blackOther"),
        value: "blackOther",
      },
      {
        label: tk("personDetails.selfDeclaredEthnicity.options.arab"),
        value: "arab",
      },
      {
        label: tk("personDetails.selfDeclaredEthnicity.options.other"),
        value: "other",
      },
      {
        label: tk("personDetails.selfDeclaredEthnicity.options.preferNotToSay"),
        value: "preferNotToSay",
      },
    ],
  })
  .withFieldConditionallyVisible({
    fieldName: "selfDeclaredEthnicityOther", // 136-person-6.1.1.1
    type: "textbox",
    ...new FieldBuilder().withSchema(shortTextSchema).build(),
    isVisible: ({ formValues }) =>
      formValues.selfDeclaredEthnicity?.value === "other",
    label: tk("personDetails.selfDeclaredEthnicityOther"),
  })
  .withFieldAlwaysVisible({
    fieldName: "hasRelativeOrFriend", // 136-person-10.0
    type: "radio",
    ...new FieldBuilder().withSchema(yesNoSchema).captureValueLabel().build(),
    label: tk("personDetails.relativeOrFriend"),
    options: [
      {
        label: tk("common.yes"),
        value: "yes",
      },
      {
        label: tk("common.no"),
        value: "no",
      },
    ],
  })
  .withFieldConditionallyVisible({
    fieldName: "relativeOrFriendForename", // 136-person-10.0.2
    type: "textbox",
    ...new FieldBuilder().withUnknowableSchema(forenameSchema).build(),
    isVisible: ({ formValues }) =>
      formValues.hasRelativeOrFriend?.value === "yes",
    label: tk("personDetails.relativeOrFriendForename"),
  })
  .withFieldConditionallyVisible({
    fieldName: "relativeOrFriendSurname", // 136-person-10.0.3
    type: "textbox",
    ...new FieldBuilder().withUnknowableSchema(surnameSchema).build(),
    isVisible: ({ formValues }) =>
      formValues.hasRelativeOrFriend?.value === "yes",
    label: tk("personDetails.relativeOrFriendSurname"),
  })
  .withFieldConditionallyVisible({
    fieldName: "relativeOrFriendRelationship", // 136-person-10.0.4
    type: "textbox",
    ...new FieldBuilder().withUnknowableSchema(longTextSchema).build(),
    isVisible: ({ formValues }) =>
      formValues.hasRelativeOrFriend?.value === "yes",
    label: tk("personDetails.relativeOrFriendRelationship"),
    multiline: true,
    rows: "5",
  })
  .withReadonlyFieldConditionallyVisible({
    fieldName: "unknownRelativeOrFriend", // 136-person-10.0.1
    type: "banner",
    bannerType: "info",
    isVisible: ({ formValues }) =>
      formValues.hasRelativeOrFriend?.value === "no",
    body: tk("personDetails.unknownRelativeOrFriend"),
  })
  .withReadonlyFieldConditionallyVisible({
    fieldName: "displayPersonAddress",
    type: "typography",
    isVisible: ({ formValues }) =>
      formValues.hasRelativeOrFriend?.value === "yes",
    textFn: ({ formValues, t }) =>
      formValues.personAddress.isUnknown
        ? [t("common.unknown")]
        : [formValues.personAddress.value?.userProvided.formattedValue].filter(
            (x) => x !== undefined,
          ),
    label: tk("personDetails.personAddress"),
  })
  .withFieldConditionallyVisible({
    fieldName: "relativeOrFriendSameAddress", // 136-person-10.0.5
    type: "radio",
    ...new FieldBuilder().withSchema(yesNoSchema).captureValueLabel().build(),
    isVisible: ({ formValues }) =>
      formValues.hasRelativeOrFriend?.value === "yes",
    label: tk("personDetails.relativeOrFriendSameAddress"),
    options: [
      {
        label: tk("common.yes"),
        value: "yes",
      },
      {
        label: tk("common.no"),
        value: "no",
      },
    ],
  })
  .withFieldConditionallyVisible({
    fieldName: "relativeOrFriendAddress", // 136-person-10.0.5.2
    type: "location",
    ...new FieldBuilder().withUnknowableSchema(LocationSchema).build(),
    isVisible: ({ formValues }) =>
      formValues.relativeOrFriendSameAddress?.value === "no",
    label: tk("personDetails.relativeOrFriendAddress"),
  })
  .withReadonlyFieldConditionallyVisible({
    fieldName: "unknownRelativeOrFriendAddress",
    type: "banner",
    bannerType: "info",
    isVisible: ({ formValues }) =>
      match(formValues.relativeOrFriendAddress)
        .with({ value: P._, isUnknown: true }, () => true)
        .with({ value: P._, isUnknown: false }, () => false)
        .with(null, undefined, () => false)
        .exhaustive(),
    body: tk("personDetails.unknownRelativeOrFriendAddress"),
  })
  .withFieldConditionallyVisible({
    fieldName: "relativeOrFriendTelephoneNumber", // 136-person-10.0.6
    type: "textbox",
    ...new FieldBuilder().withUnknowableSchema(shortTextSchema).build(),
    isVisible: ({ formValues }) =>
      formValues.hasRelativeOrFriend?.value === "yes",
    label: tk("personDetails.relativeOrFriendTelephone"),
    inputType: "tel",
  })
  .withFieldConditionallyVisible({
    fieldName: "relativeOrFriendInformed", // 136-person-10.0.7
    type: "radio",
    ...new FieldBuilder().withSchema(yesNoSchema).captureValueLabel().build(),
    isVisible: ({ formValues }) =>
      formValues.hasRelativeOrFriend?.value === "yes",
    label: tk("personDetails.relativeOrFriendInformed"),
    options: [
      {
        label: tk("common.yes"),
        value: "yes",
      },
      {
        label: tk("common.no"),
        value: "no",
      },
    ],
  })
  .withFieldConditionallyVisible({
    fieldName: "reasonNotInformed", // 136-person-10.0.7.1
    type: "textbox",
    ...new FieldBuilder().withSchema(longTextSchema).build(),
    isVisible: ({ formValues }) =>
      formValues.relativeOrFriendInformed?.value === "no",
    label: tk("personDetails.reasonNotInformed"),
    multiline: true,
    rows: "5",
  })
  .withPage([
    "personForename", // 136-person-1.0
    "personSurname", // 136-person-1.1
  ])
  .withPage([
    "personAddress", // 136-person-2.0
    "confirmPersonAddress", // 136-person-2.0.1
  ])
  .withPage([
    "dateOfBirth", // 136-person-3.0
    "satisfiedPersonIsOver18", // 136-person-3.0.1
  ])
  .withPage([
    "gender", // 136-person-5.0
  ])
  .withPage([
    "ethnicAppearance", // 136-person-6.0
    "selfDeclaredEthnicity", // 136-person-6.1
    "selfDeclaredEthnicityOther", // 136-person-6.1.1.1
  ])
  .withPage([
    "hasRelativeOrFriend", // 136-person-10.0
    "relativeOrFriendForename", // 136-person-10.0.2
    "relativeOrFriendSurname", // 136-person-10.0.3
    "relativeOrFriendRelationship", // 136-person-10.0.4
    "unknownRelativeOrFriend", // 136-person-10.0.1
  ])
  .withPage([
    "displayPersonAddress",
    "relativeOrFriendSameAddress", // 136-person-10.0.5
    "relativeOrFriendAddress", // 136-person-10.0.5.2
    "unknownRelativeOrFriendAddress",
  ])
  .withPage([
    "relativeOrFriendTelephoneNumber", // 136-person-10.0.6
    "relativeOrFriendInformed", // 136-person-10.0.7
    "reasonNotInformed", // 136-person-10.0.7.1
  ])
  .build({ t });

export type PersonDetailsFormData = ReturnType<
  typeof formSpecification
>["formType"];
